import React, { useEffect, useState } from 'react';
import html from 'remark-html';
import markdown from 'remark-parse';
import unified from 'unified';
import PropTypes from 'prop-types';
import { ArrowLeft, Coffee, Calendar, Rss } from 'akar-icons';
import { Helmet } from 'react-helmet';

// Components
import SEO from '../../components/SEO/SEO';
import Micro from '../../layouts/Micro';
import SocialButtonGroup from '../../components/SocialButtonGroup/SocialButtonGroup';
import ReadArticleButton from '../../components/ReadArticleButton/ReadArticleButton';
import Image from '../../components/Image/Image';
import AuthorCard from '../../components/AuthorCard/AuthorCard';
import Link from '../../components/Link/Link';
import PostListItem from '../../components/Post/PostListItem/PostListItem';

// Utils
import calculateReadingTime from '../../utils/calculateReadingTime';
import generateCategoryColor from '../../utils/generateCategoryColor';
import stripHTML from '../../utils/stripHTML';
// SEO Schema
import BlogPostingSchema from '../../components/SEO/Schemas/BlogPostingSchema';
import HowToSchema from '../../components/SEO/Schemas/HowToSchema';
import PersonSchema from '../../components/SEO/Schemas/PersonSchema';
import SpeakableSchema from '../../components/SEO/Schemas/SpeakableSchema';

// Style
import './blog-post-detail.scss';

const Post = ({ pageContext, location }) => {
  const [link, setLink] = useState();

  useEffect(() => {
    setLink(location.href);
  }, [location.href]);

  const {
    title,
    content,
    excerpt,
    modified,
    tags,
    categories,
    featuredImage,
    author,
  } = pageContext.post;

  const { allPosts } = pageContext;

  const relatedArticles =
    allPosts &&
    allPosts
      .filter(
        (post) =>
          post.categories.edges[0].node.name === categories.edges[0]?.node.name &&
          post.title !== title
      )
      .slice(0, 3);

  const authorInfo =
    author.node.firstName && author.node.lastName
      ? `${author.node.firstName} ${author.node.lastName}`
      : author.node.name;

  const checkPostValues = (post) => {
    if (post?.categories?.edges?.length && post?.featuredImage?.node?.sourceUrl) {
      return true;
    }
    return false;
  };

  const blogPostContent = unified().use(markdown).use(html).processSync(content);

  return (
    <Micro>
      <div className="post-detail-container">
        <SEO title={title} description={excerpt} />
        <Helmet>
          <script type="application/ld+json">
            {BlogPostingSchema(
              title,
              featuredImage?.node?.sourceUrl,
              authorInfo,
              'RemoteTeam',
              'https://res.cloudinary.com/hebu10lmu/image/upload/c_scale,q_auto,h_110/v1597065935/www/forestry/remote-team-blue-logo_mhisgu.png',
              modified,
              modified
            )}
          </script>
          {title.includes('How to') && (
            <script type="application/ld+json">
              {HowToSchema(
                title,
                title,
                featuredImage?.node?.sourceUrl,
                stripHTML(excerpt),
                title,
                location.href
              )}
            </script>
          )}
          <script type="application/ld+json">
            {PersonSchema(authorInfo, author?.node?.avatar?.url)}
          </script>
          <script type="application/ld+json">
            {SpeakableSchema(title, location.href, ['#blog-article'])}
          </script>
        </Helmet>

        <div className="container-fluid p-0 ml-0 mr-0" style={{ backgroundColor: 'white' }}>
          <div className="post-box">
            <div className="row m-0">
              <div className="col-lg-3 col-md-6 col-sm-6 col-xs-6 flex flex-middle">
                <div className="post-details container">
                  {/* Back to blog */}
                  <div className="tag-area text-uppercase mb-1">
                    <Link href="/blog" rel="noopeneer noreferrer" title="Back to blog">
                      <ArrowLeft
                        strokeWidth={4}
                        size={13}
                        style={{
                          marginBottop: '-0.6rem',
                          marginRight: '0.5rem',
                        }}
                      />
                      Back to blog
                    </Link>
                    <span className="ml-1 mr-1"> | </span>
                    <Link
                      href={`/blog/tags/${categories.edges[0]?.node.slug}`}
                      rel="noopeneer noreferrer"
                      title={categories.edges[0]?.node.name}
                      style={{
                        color: generateCategoryColor(categories.edges[0]?.node.slug),
                      }}
                    >
                      {categories.edges[0]?.node.name}
                    </Link>
                  </div>

                  {/* Title */}
                  <h1 className="mb-2 mt-1">{title}</h1>
                  {/* Title End */}

                  <div className="row flex-middle flex-center">
                    <div className="col-lg-1 p-0">
                      {/* Author Area */}
                      <div className="author-area flex flex-start flex-middle">
                        <img src={author.node.avatar.url} alt={author.node.name && authorInfo} />
                        <p className="text-center">{authorInfo}</p>
                      </div>
                      {/* Author Area End */}
                    </div>

                    <div className="col-lg-5 p-0">
                      <div className="row flex-middle flex-start">
                        {/* Reading Time */}
                        <div className="col-lg-2 col-md-2 col-sm-2 col-xs-4 p-0">
                          <div className="reading-time flex flex-center flex-middle">
                            <Coffee strokeWidth={3} size={19} />
                            <p>{content && calculateReadingTime(content)} read</p>
                          </div>
                        </div>
                        {/* Reading Time End */}

                        {/* Published Date */}
                        <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 p-0">
                          <div className="published-date flex flex-center flex-middle">
                            <Calendar strokeWidth={3} size={19} />
                            <p>
                              {modified ? modified.slice(0, 11) : new Date().toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        {/* Published Date End */}

                        {/* RSS Feed */}
                        <div className="col-lg-2 col-md-2 col-sm-2 col-xs-6 p-0">
                          <div className="rss-feed flex flex-center flex-middle">
                            <Rss strokeWidth={3} size={19} />
                            <a href="/rss.xml" target="_blank" title="Subscribe to RSS">
                              <p>Subscribe</p>
                            </a>
                          </div>
                        </div>
                        {/* RSS Feed End */}
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  {tags.edges.length > 0 && (
                    <div className="row">
                      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6 pl-0">
                        <ul className="tags-list flex flex-middle">
                          <li>
                            <span>TAGS: </span>
                          </li>
                          {tags.edges.map(({ node }, index) => {
                            const listLength = tags.edges.length;
                            return (
                              <li key={node.slug}>
                                <Link
                                  rel="noreferrer"
                                  href={`/blog/tags/${node.slug}`}
                                  key={node.slug}
                                  title={node.name}
                                >
                                  {listLength === index + 1 ? `${node.name}` : `${node.name},`}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  )}
                  {/* Tags End */}

                  {/* Read Article */}
                  <div className="row mt-1">
                    <div className="col-lg-6 col-md-3 col-sm-3 col-xs-6">
                      <div className="speaking-area flex flex-middle">
                        <ReadArticleButton content={stripHTML(content)} />
                      </div>
                    </div>
                  </div>
                  {/* Read Article End */}
                </div>
              </div>

              {/* Featured Image */}
              <div className="col-lg-3 col-md-6 col-sm-6 col-xs-6 last-lg first-xs p-0">
                <Image
                  img={featuredImage?.node?.sourceUrl}
                  customClass="post-image"
                  alt={title}
                  radiusBottomLeft={20}
                />
              </div>
              {/* Featured Image */}
            </div>
          </div>
        </div>

        <div className="pt-4" style={{ backgroundColor: '#FAFAFA' }}>
          <div className="container">
            <div className="row flex-start">
              <div className="col-lg-1 col-md-1 col-sm-1 display-xs-none">
                <div className="row flex-column start-lg center-xs sticky-group">
                  <SocialButtonGroup url={link} title={title} summary={excerpt} />
                </div>
              </div>
              <div className="col-lg-5 col-md-5 col-sm-5 col-xs-6 mb-1">
                <div className="mt-0 mb-2">
                  <article
                    id="blog-article"
                    className="article text-dark img-responsive"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: blogPostContent,
                    }}
                  />
                </div>
                <div className="my-3">
                  <AuthorCard
                    img={author.node.avatar.url}
                    name={author.node.name && authorInfo}
                    desc={author.node.description}
                  />
                </div>
                {/* Related Articles */}
                <h3 className="text-blue text-center mt-4 mb-1">Related Articles</h3>
                <div className="row">
                  {relatedArticles &&
                    relatedArticles.filter(checkPostValues).map((post) => {
                      const postData = {
                        title: post.title,
                        slug: post.slug,
                        content: post.content,
                        featuredImage: post?.featuredImage?.node?.sourceUrl,
                        excerpt: post.excerpt,
                        categoryName: post.categories.edges[0].node.name,
                        categorySlug: post.categories.edges[0].node.slug,
                      };
                      return <PostListItem key={post.slug} post={postData} />;
                    })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Micro>
  );
};

Post.propTypes = {
  pageContext: PropTypes.object.isRequired, // eslint-disable-line
  location: PropTypes.object.isRequired, // eslint-disable-line
};

export default Post;
